import { useEffect, useState } from 'react';

/**
 * Whether the element behind `ref` is on screen.
 *
 * Uses the viewport as the root, so it also works when the element sits inside a
 * scrolling container (the gallery scrolls inside MasonryList, not the window).
 *
 * @param {{ current: Element|null }} ref
 * @param {{ rootMargin?: string, initialInView?: boolean }} [options]
 * @returns {boolean} `false` when there is no element or no IntersectionObserver.
 */
const useIsInView = (ref, { rootMargin = '0px', initialInView = true } = {}) => {
  const [isInView, setIsInView] = useState(initialInView);

  useEffect(() => {
    const element = ref?.current;
    if (!element || typeof window === 'undefined' || !('IntersectionObserver' in window)) {
      setIsInView(false);
      return undefined;
    }

    const observer = new window.IntersectionObserver(([entry]) => setIsInView(entry.isIntersecting), {
      rootMargin,
    });
    observer.observe(element);

    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isInView;
};

export default useIsInView;
